import { useCallback, useMemo, useState } from 'react';
import { ArrowLeft, RefreshCw, Receipt } from 'lucide-react';
import apiService from '../../services/api';

const TABS = [
  { id: 'ALL', label: 'Toutes' },
  { id: 'DAILY', label: 'Jour' },
  { id: 'MONTHLY', label: 'Mois' },
  { id: 'ANNUAL', label: 'Année' },
];

function formatEuro(cents) {
  return `${(Number(cents || 0) / 100).toFixed(2)} €`;
}

export default function FiscalClosuresPanel({
  commercantId,
  onBack,
  fiscalClosures,
  fiscalPerpetual,
  fiscalArchives,
  fiscalClosePeriodKey,
  setFiscalClosePeriodKey,
  fiscalCloseBusy,
  fiscalCloseMsg,
  fiscalArchiveBusy,
  fiscalArchiveMsg,
  onRefresh,
  onCloseDaily,
  onGenerateArchive,
  onDownloadArchive,
}) {
  const [tab, setTab] = useState('ALL');
  const [detail, setDetail] = useState(null);
  const [detailMsg, setDetailMsg] = useState('');

  const filtered = useMemo(() => {
    if (tab === 'ALL') return fiscalClosures;
    return fiscalClosures.filter((c) => c.closureType === tab);
  }, [fiscalClosures, tab]);

  const openDetail = useCallback(
    async (c) => {
      setDetailMsg('');
      try {
        const res = await apiService.getFiscalClosure(c.closureType, c.periodKey, commercantId);
        setDetail(res.closure || res);
      } catch (e) {
        setDetail(null);
        setDetailMsg(e.message || 'Détail indisponible');
      }
    },
    [commercantId]
  );

  return (
    <div className="pos-caisse-work-surface" data-testid="caisse-fiscal-closures-panel">
      <header className="pos-caisse-work-surface-header flex flex-wrap items-center justify-between gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          className="pos-touch-target flex items-center gap-2 text-slate-600 hover:text-slate-900 min-h-[44px] px-2 rounded-xl hover:bg-slate-100"
        >
          <ArrowLeft className="h-5 w-5" /> Retour
        </button>
        <h2 className="text-xl font-bold text-slate-900 flex-1 min-w-[8rem]">Clôtures fiscales</h2>
        <button type="button" onClick={onRefresh} className="pos-touch-target p-2 rounded-xl hover:bg-slate-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
          <RefreshCw className="h-5 w-5 text-teal-600" />
        </button>
      </header>

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`caisse-fiscal-closure-tab-${t.id.toLowerCase()}`}
            onClick={() => {
              setTab(t.id);
              setDetail(null);
            }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              tab === t.id ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 space-y-3">
        <p className="text-sm text-slate-600">
          Distinct de la clôture de session. Consultation ci-dessous ; la clôture d&apos;un jour est
          une action séparée et irréversible côté serveur.
        </p>
        {fiscalPerpetual ? (
          <p className="text-sm font-medium text-slate-800">
            Total perpétuel : {formatEuro(fiscalPerpetual.revenueCents)} ·{' '}
            {fiscalPerpetual.salesCount || 0} vente(s)
          </p>
        ) : null}
        <details className="text-sm">
          <summary className="cursor-pointer font-medium text-teal-800">
            Actions avancées (clôture / archive)
          </summary>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-sm text-slate-600">
              Jour (Europe/Paris)
              <input
                type="date"
                value={fiscalClosePeriodKey}
                onChange={(e) => setFiscalClosePeriodKey(e.target.value)}
                className="mt-1 block rounded-xl border border-slate-200 px-3 py-2"
              />
            </label>
            <button
              type="button"
              disabled={fiscalCloseBusy}
              onClick={onCloseDaily}
              className="rounded-xl bg-teal-600 text-white px-4 py-2 font-semibold hover:bg-teal-700 disabled:opacity-50"
            >
              {fiscalCloseBusy ? 'Clôture…' : 'Clôturer le jour'}
            </button>
            <button
              type="button"
              disabled={fiscalArchiveBusy}
              onClick={onGenerateArchive}
              className="rounded-xl bg-slate-800 text-white px-4 py-2 font-semibold disabled:opacity-50"
            >
              {fiscalArchiveBusy ? 'Archivage…' : 'Archiver le jour'}
            </button>
          </div>
        </details>
        {fiscalCloseMsg ? (
          <p className="text-sm text-slate-700 bg-slate-50 rounded-xl px-3 py-2">{fiscalCloseMsg}</p>
        ) : null}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
          <Receipt className="h-10 w-10 mx-auto text-slate-200 mb-2" />
          <p className="text-slate-500">Aucune clôture pour ce filtre</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                data-testid="caisse-fiscal-closure-row"
                data-closure-type={c.closureType}
                data-period-key={c.periodKey}
                onClick={() => openDetail(c)}
                className="w-full bg-white rounded-2xl border border-slate-100 p-4 flex justify-between gap-3 text-left hover:border-teal-300"
              >
                <div>
                  <p className="font-semibold text-slate-900">
                    {c.closureType} · {c.periodKey}
                  </p>
                  <p className="text-xs text-slate-500">
                    {c.status} · {c.salesCount} vente(s)
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-900">{formatEuro(c.revenueCents)}</p>
                  <p className="text-xs text-slate-500">encaissé {formatEuro(c.paymentsCents)}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {detail ? (
        <div
          className="mt-4 bg-teal-50 border border-teal-100 rounded-2xl p-4 text-sm"
          data-testid="caisse-fiscal-closure-detail"
        >
          <p className="font-bold">
            Détail {detail.closureType} · {detail.periodKey}
          </p>
          <p className="mt-1">Statut : {detail.status}</p>
          <p>CA : {formatEuro(detail.revenueCents)} · TVA / paiements : {formatEuro(detail.paymentsCents)}</p>
          <p>Ventes : {detail.salesCount}</p>
          {detail.eventSequenceFrom ? (
            <p className="text-xs text-slate-600 mt-1">
              Événements journal {detail.eventSequenceFrom} → {detail.eventSequenceTo}
            </p>
          ) : null}
        </div>
      ) : null}
      {detailMsg ? <p className="text-sm text-red-700 mt-2">{detailMsg}</p> : null}

      <div className="bg-white rounded-2xl border border-slate-100 p-4 mt-6 space-y-3">
        <h3 className="font-bold text-slate-900">Archives fiscales</h3>
        <p className="text-sm text-slate-600">
          Export ZIP local lorsque disponible. Ce n&apos;est pas une attestation de conformité.
        </p>
        {fiscalArchiveMsg ? (
          <p className="text-sm text-slate-700 bg-slate-50 rounded-xl px-3 py-2">{fiscalArchiveMsg}</p>
        ) : null}
        {fiscalArchives.length > 0 ? (
          <ul className="space-y-2 pt-2">
            {fiscalArchives.map((a) => (
              <li
                key={a.id}
                className="flex justify-between gap-3 items-center border border-slate-100 rounded-xl p-3"
              >
                <div>
                  <p className="font-semibold text-sm">
                    {a.periodType} · {a.periodKey}
                  </p>
                  <p className="text-xs text-slate-500">
                    {a.status} · complétude {a.completenessStatus}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="caisse-fiscal-archive-download"
                  className="text-sm font-medium text-teal-700 hover:underline"
                  onClick={() => onDownloadArchive(a.id)}
                >
                  Télécharger
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Aucune archive pour le moment</p>
        )}
      </div>
    </div>
  );
}
